import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { getErrorMessage } from "@/lib/utils";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { User, Mail, Phone, MapPin, Save, Loader2, CheckCircle2, Camera, Shield, Briefcase, ArrowRight, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";



const ProfilePage = () => {
  const { user, updateProfile, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [switchingMode, setSwitchingMode] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    setUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/upload/profile-picture`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      if (refreshUser) await refreshUser();
      toast.success("Profile picture updated!");
    } catch (err) {
      toast.error(getErrorMessage(err, "Upload failed"));
    }
    setUploadingPicture(false);
  };
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: ""
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || ""
      });
    }
  }, [user]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error("Name is required");
      return;
    }

    setLoading(true);
    try {
      await updateProfile({
        name: formData.name,
        phone: formData.phone,
        address: formData.address
      });
      toast.success("Profile updated successfully!");
    } catch (error) {
      const message = getErrorMessage(error, "Failed to update profile");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="profile-page">
      <Navbar />

      {/* Hero */}
      <section className="hero-bg pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="heading-2 text-white mb-4">My Profile</h1>
            <p className="body-lg text-stone-300">
              Manage your personal information
            </p>
          </motion.div>
        </div>
      </section>

      {/* Profile Form */}
      <section className="section-container py-12">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-8"
          >
            {/* Avatar */}
            <div className="flex flex-col items-center mb-8">
              <div className="relative group">
                {user?.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="w-24 h-24 rounded-full object-cover border-4 border-lime-400"
                    data-testid="profile-avatar-img"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500 to-lime-400 flex items-center justify-center border-4 border-lime-400">
                    <span className="text-2xl font-bold text-white">{getInitials(formData.name)}</span>
                  </div>
                )}
                <label className="absolute bottom-0 right-0 w-8 h-8 bg-emerald-950 rounded-full flex items-center justify-center border-2 border-white cursor-pointer hover:bg-emerald-800 transition-colors" data-testid="profile-upload-picture-label">
                  {uploadingPicture ? (
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4 text-white" />
                  )}
                  <input type="file" accept="image/*" className="hidden" onChange={handlePictureUpload} disabled={uploadingPicture} data-testid="profile-picture-input" />
                </label>
              </div>
              <h2 className="text-xl font-semibold text-emerald-950 mt-4">{formData.name || "User"}</h2>
              <p className="text-stone-500">{formData.email}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <Label className="text-emerald-950 font-medium mb-2 block">
                  <User className="w-4 h-4 inline mr-2" />
                  Full Name *
                </Label>
                <Input
                  className="input-style"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  data-testid="profile-name-input"
                />
              </div>

              {/* Email (read-only) */}
              <div>
                <Label className="text-emerald-950 font-medium mb-2 block">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email Address
                </Label>
                <Input
                  className="input-style bg-stone-100"
                  value={formData.email}
                  disabled
                  data-testid="profile-email-input"
                />
                <p className="text-xs text-stone-500 mt-1">Email cannot be changed</p>
              </div>

              {/* Phone */}
              <div>
                <Label className="text-emerald-950 font-medium mb-2 block">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Phone Number
                </Label>
                <Input
                  className="input-style"
                  placeholder="(416) 555-0123"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  data-testid="profile-phone-input"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="btn-primary w-full"
                disabled={loading}
                data-testid="save-profile-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </form>

            {/* Account Info */}
            <div className="mt-8 pt-6 border-t border-stone-200">
              <h3 className="font-semibold text-emerald-950 mb-4">Account Information</h3>
              <div className="space-y-2 text-sm text-stone-600">
                <div className="flex justify-between">
                  <span>Account Type</span>
                  <span className="font-medium capitalize">{user?.auth_provider === "google" ? "Google Account" : "Email Account"}</span>
                </div>
                <div className="flex justify-between">
                  <span>Account Mode</span>
                  <Badge className={user?.account_mode === "professional" ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-700"} data-testid="account-mode-badge">
                    {user?.account_mode === "professional" ? "Professional" : "Personal"}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Professional Account Section */}
            <div className="mt-6 pt-6 border-t border-stone-200" data-testid="professional-section">
              <h3 className="font-semibold text-emerald-950 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-600" />
                Professional Account
              </h3>

              {user?.professional_status === "approved" ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl border border-green-200">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <div className="flex-1">
                      <p className="font-medium text-green-800 text-sm">Verified Professional</p>
                      <p className="text-xs text-green-600">Services: {user?.professional_services?.join(", ")}</p>
                    </div>
                  </div>

                  {user?.account_mode === "personal" ? (
                    <Button
                      className="w-full bg-emerald-950 hover:bg-emerald-900 text-white rounded-full"
                      onClick={async () => {
                        setSwitchingMode(true);
                        try {
                          await axios.patch(`${API}/user/account-mode`, { mode: "professional" }, { withCredentials: true });
                          if (refreshUser) await refreshUser();
                          toast.success("Switched to Professional Mode!");
                          navigate("/professional/dashboard");
                        } catch (err) {
                          toast.error(getErrorMessage(err, "Failed to switch mode"));
                        } finally {
                          setSwitchingMode(false);
                        }
                      }}
                      disabled={switchingMode}
                      data-testid="switch-to-pro-btn"
                    >
                      {switchingMode ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Briefcase className="w-4 h-4 mr-2" />}
                      Switch to Professional Mode
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <Link to="/professional/dashboard">
                        <Button className="w-full bg-emerald-950 hover:bg-emerald-900 text-white rounded-full" data-testid="go-to-pro-dashboard-btn">
                          <Briefcase className="w-4 h-4 mr-2" />
                          Open Professional Dashboard
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        className="w-full rounded-full"
                        onClick={async () => {
                          setSwitchingMode(true);
                          try {
                            await axios.patch(`${API}/user/account-mode`, { mode: "personal" }, { withCredentials: true });
                            if (refreshUser) await refreshUser();
                            toast.success("Switched to Personal Mode");
                            navigate("/");
                          } catch (err) {
                            toast.error("Failed to switch mode");
                          } finally {
                            setSwitchingMode(false);
                          }
                        }}
                        disabled={switchingMode}
                        data-testid="switch-to-personal-btn"
                      >
                        Switch to Personal Mode
                      </Button>
                    </div>
                  )}
                </div>
              ) : user?.professional_status === "pending" ? (
                <div className="flex items-center gap-3 p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                  <div className="flex-1">
                    <p className="font-medium text-yellow-800 text-sm">Application Under Review</p>
                    <p className="text-xs text-yellow-600">We'll notify you once your verification is complete</p>
                  </div>
                </div>
              ) : user?.professional_status === "rejected" ? (
                <div className="flex items-center gap-3 p-4 bg-red-50 rounded-xl border border-red-200">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <div className="flex-1">
                    <p className="font-medium text-red-800 text-sm">Application Rejected</p>
                    <p className="text-xs text-red-600">Contact support for more information</p>
                  </div>
                </div>
              ) : (
                <div className="text-center p-6 bg-stone-50 rounded-xl border border-stone-200">
                  <Shield className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-600 text-sm mb-4">Want to offer services on Stack? Become a verified professional.</p>
                  <Link to="/professional/verify">
                    <Button className="bg-emerald-950 hover:bg-emerald-900 text-white rounded-full px-8" data-testid="become-professional-btn">
                      <Shield className="w-4 h-4 mr-2" />
                      Become a Professional
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProfilePage;
